import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { Product, Category, PRINT_SIZES } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Search } from "lucide-react";
import { useTenant } from "../lib/tenant";

const SIDEBAR_SIZES = [...PRINT_SIZES].reverse() as string[];
const SIZE_LABEL: Record<string, string> = { A6: "A6 (Card)" };

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const { i18n } = useTranslation();
  const isJa = i18n.language.startsWith("ja");
  const { id: tenantId, tTenant } = useTenant();

  const selectedSizes = searchParams.getAll("size");
  const selectedCategories = searchParams.getAll("category");

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: () => api.get("/products/categories").then((r) => Array.isArray(r.data) ? r.data : []),
  });

  const queryString = new URLSearchParams();
  if (search) queryString.set("search", search);
  for (const sz of selectedSizes) queryString.append("size", sz);
  for (const cat of selectedCategories) queryString.append("category", cat);

  const { data: products = [], isLoading } = useQuery<Product[]>({
    queryKey: ["products", selectedCategories.join(","), search, selectedSizes.join(",")],
    queryFn: () =>
      api.get(`/products?${queryString.toString()}`).then((r) => Array.isArray(r.data) ? r.data : []),
  });

  const countQueryString = new URLSearchParams();
  if (search) countQueryString.set("search", search);
  const { data: allProducts = [] } = useQuery<Product[]>({
    queryKey: ["products-count", search],
    queryFn: () =>
      api.get(`/products?${countQueryString.toString()}`).then((r) => Array.isArray(r.data) ? r.data : []),
  });

  const sizeCount = (sz: string) => allProducts.filter((p) => p.sizePrices?.some((sp) => sp.size === sz)).length;
  const catCount = (slug: string) => allProducts.filter((p) => p.categorySlug === slug).length;

  function toggleSize(sz: string) {
    const params = new URLSearchParams(searchParams);
    const current = params.getAll("size");
    params.delete("size");
    if (current.includes(sz)) {
      current.filter((s) => s !== sz).forEach((s) => params.append("size", s));
    } else {
      [...current, sz].forEach((s) => params.append("size", s));
    }
    setSearchParams(params);
  }

  function toggleCategory(slug: string) {
    const params = new URLSearchParams(searchParams);
    const current = params.getAll("category");
    params.delete("category");
    if (current.includes(slug)) {
      current.filter((c) => c !== slug).forEach((c) => params.append("category", c));
    } else {
      [...current, slug].forEach((c) => params.append("category", c));
    }
    setSearchParams(params);
  }

  const isChlorine = tenantId === "chlorine.art";

  return (
    <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 ${isChlorine ? "font-display" : ""}`}>
      <div className={isChlorine ? "text-center mb-16" : "mb-8"}>
        <h1 className={`text-4xl font-bold text-gray-900 mb-2 ${isChlorine ? "tracking-tight uppercase" : ""}`}>
          {tTenant("shop.title")}
        </h1>
        <p className="text-gray-500 uppercase tracking-widest text-xs font-semibold">
          {isChlorine ? "The Premium Collection" : tTenant("shop.subAll")}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-12">
        <aside className="lg:w-64 flex-shrink-0">
          <div className="relative mb-8">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={tTenant("shop.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10 bg-gray-50 border-transparent focus:bg-white transition-all"
            />
          </div>

          <nav className="space-y-8">
            <div>
              <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-4">{tTenant("shop.occasion")}</h2>
              <ul className="space-y-3">
                {categories.filter((c) => !c.hidden).map((cat) => (
                  <li key={cat.id}>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.slug)}
                        onChange={() => toggleCategory(cat.slug)}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        style={{ accentColor: 'rgb(var(--brand-color))' }}
                      />
                      <span className={`text-sm font-medium transition-colors ${selectedCategories.includes(cat.slug) ? "text-gray-900" : "text-gray-500 group-hover:text-gray-900"}`} style={selectedCategories.includes(cat.slug) ? { color: 'rgb(var(--brand-color))' } : {}}>
                        {isJa && cat.nameJa ? cat.nameJa : cat.name} <span className="text-[10px] opacity-50 ml-1">({catCount(cat.slug)})</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-4">Print Size</h2>
              <ul className="space-y-3">
                {SIDEBAR_SIZES.map((sz) => (
                  <li key={sz}>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedSizes.includes(sz)}
                        onChange={() => toggleSize(sz)}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        style={{ accentColor: 'rgb(var(--brand-color))' }}
                      />
                      <span className={`text-sm font-medium transition-colors ${selectedSizes.includes(sz) ? "text-gray-900" : "text-gray-500 group-hover:text-gray-900"}`} style={selectedSizes.includes(sz) ? { color: 'rgb(var(--brand-color))' } : {}}>
                        {SIZE_LABEL[sz] ?? sz} <span className="text-[10px] opacity-50 ml-1">({sizeCount(sz)})</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </aside>

        <div className="flex-1">
          {isLoading ? (
            <div className={`grid gap-8 ${isChlorine ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"}`}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl bg-gray-100 animate-pulse aspect-[4/5]" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center text-gray-500 uppercase tracking-widest text-sm">{tTenant("shop.empty")}</div>
          ) : (
            <div className={`grid gap-x-8 gap-y-12 ${isChlorine ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"}`}>
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
